/**
 * The operation log of the Control Room ("Felhasználói műveletek", audit plan F4): its filters,
 * the shape the backend answers with, and what the tab shows of an entry.
 *
 * Reference: documentation/implementation_plan_audit_log.md (F4, T6, T9)
 */

export type AuditLogSource = 'app' | 'bridge' | 'system' | 'legacy';
export type AuditLogLevel = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface AuditLogFilters {
    /**
     * First and last day shown, `YYYY-MM-DD`, read by the backend as the shop's days. The tab opens
     * on this computer's today, as the rest of the Control Room does.
     */
    fromDate?: string;
    toDate?: string;
    userId?: number;
    area?: string;
    action?: string;
    source?: AuditLogSource;
    contract?: string;
    clientId?: number;
}

export interface AuditLogListItem {
    id: string;
    occurredAt: string | null;
    userId: number | null;
    username: string | null;
    source: AuditLogSource;
    entityType: string;
    entityId: number | null;
    entityKey: string | null;
    action: string;
    summary: string | null;
    logLevel: AuditLogLevel;
    amount: number | null;
    reference: string | null;
}

export interface AuditLogEntry extends AuditLogListItem {
    ipAddress: string | null;
    userAgent: string | null;
    bridgeEventId: string | null;
    oldValues: Record<string, unknown> | null;
    newValues: Record<string, unknown> | null;
}

export interface AuditLogPagination {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    /**
     * The newest log row the list was opened with, passed back with its later pages: every page
     * read is itself logged, and without it each page moved the list on by one row.
     */
    upToId?: number | null;
}

export interface AuditLogCatalogue {
    areas: Array<{ area: string; actions: string[] }>;
    sources: AuditLogSource[];
}

const DAY_FORMAT_LENGTH = 10;

/** `YYYY-MM-DD` of a date on this computer's clock. */
export const localDay = (date: Date): string => {
    const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return shifted.toISOString().slice(0, DAY_FORMAT_LENGTH);
};

/**
 * A time as the log answers it: the shop's clock, `2026-10-28 11:15:00`, with no zone. It is shown
 * as it is - never through `Date` or the browser's zone - so it matches the shop's clock and its
 * cameras on any computer. Null when there is none.
 */
export const shopTimeText = (value: string | null): string | null =>
    value === null || value.trim() === '' ? null : value;

/** What the tab opens with: today. Older days are one filter away. */
export const defaultAuditLogFilters = (now: Date): AuditLogFilters => ({
    fromDate: localDay(now),
    toDate: localDay(now),
});

/**
 * The query string of one page. No offset from UTC goes along: the backend reads "a day" as the
 * shop's day, with the offset in force on that date - one offset taken today moved a day across a
 * daylight-saving change by an hour.
 */
export const buildAuditLogSearchParams = (
    filters: AuditLogFilters,
    page: number,
    pageSize: number,
    upToId?: number | null,
): string => {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
    });
    const values: Array<[string, string | number | null | undefined]> = [
        ['from', filters.fromDate],
        ['to', filters.toDate],
        ['userId', filters.userId],
        ['area', filters.area],
        ['action', filters.action],
        ['source', filters.source],
        ['contract', filters.contract?.trim()],
        ['clientId', filters.clientId],
        ['upToId', upToId],
    ];
    for (const [name, value] of values) {
        if (value !== undefined && value !== null && value !== '') {
            params.set(name, String(value));
        }
    }
    return params.toString();
};

/** The codes to offer in the operation filter: one area's, or every area's. */
export const actionsForArea = (catalogue: AuditLogCatalogue | null, area?: string): string[] => {
    if (catalogue === null) {
        return [];
    }
    const areas =
        area === undefined ? catalogue.areas : catalogue.areas.filter(entry => entry.area === area);
    return areas.flatMap(entry => entry.actions);
};

/** The area an action belongs to, for a filter that keeps the two consistent. */
export const areaOfAction = (
    catalogue: AuditLogCatalogue | null,
    action: string,
): string | undefined => catalogue?.areas.find(entry => entry.actions.includes(action))?.area;

export const actionLabelKey = (action: string): string => `controlRoom.auditLog.actions.${action}`;
export const areaLabelKey = (area: string): string => `controlRoom.auditLog.areas.${area}`;
export const sourceLabelKey = (source: string): string => `controlRoom.auditLog.sources.${source}`;
export const levelLabelKey = (level: string): string => `controlRoom.auditLog.levels.${level}`;

const SOURCE_COLORS: Record<AuditLogSource, string> = {
    app: 'blue',
    bridge: 'purple',
    system: 'default',
    legacy: 'gold',
};

export const sourceTagColor = (source: string): string =>
    SOURCE_COLORS[source as AuditLogSource] ?? 'default';

const LEVEL_COLORS: Record<AuditLogLevel, string | undefined> = {
    INFO: undefined,
    WARNING: 'orange',
    ERROR: 'red',
    CRITICAL: 'magenta',
};

/** A tag colour for a level worth noticing; none for an ordinary row. */
export const levelTagColor = (level: string): string | undefined =>
    LEVEL_COLORS[level as AuditLogLevel];

/** What the row concerns: `CONTRACT 314279`, `ENDPOINT GET /api/clients`, or the type alone. */
export const describeAuditEntity = (
    item: Pick<AuditLogListItem, 'entityType' | 'entityId' | 'entityKey'>,
): string => {
    const reference = item.entityId ?? item.entityKey;
    return reference === null ? item.entityType : `${item.entityType} ${reference}`;
};

/** The log row id behind a list id (`log-12` -> 12); null for a legacy row. */
export const auditLogEntryId = (listId: string): number | null => {
    const match = /^log-(\d+)$/.exec(listId);
    return match === null ? null : Number(match[1]);
};

/**
 * Whether an entry has more to show than its row: only the log's own rows do (T9 B). The same
 * test that opens it, so a Details button never does nothing.
 */
export const hasAuditDetail = (item: Pick<AuditLogListItem, 'id'>): boolean =>
    auditLogEntryId(item.id) !== null;

export interface AuditValueRow {
    field: string;
    before: unknown;
    after: unknown;
    /** Whether the value differs; a field recorded on one side only always does. */
    changed: boolean;
}

const sameValue = (left: unknown, right: unknown): boolean =>
    JSON.stringify(left) === JSON.stringify(right);

/**
 * The fields of an entry, side by side. Every field of either side is a row, in the order they
 * were recorded, the new side's extra fields after the old side's.
 */
export const auditValueRows = (
    oldValues: Record<string, unknown> | null,
    newValues: Record<string, unknown> | null,
): AuditValueRow[] => {
    const before = oldValues ?? {};
    const after = newValues ?? {};
    const beforeFields = new Set(Object.keys(before));
    const afterFields = new Set(Object.keys(after));
    const fields = [...new Set([...beforeFields, ...afterFields])];
    return fields.map(field => {
        const inBefore = beforeFields.has(field);
        const inAfter = afterFields.has(field);
        // Read only what was recorded: `before.toString` on a side without it is Object's own.
        const beforeValue = inBefore ? before[field] : undefined;
        const afterValue = inAfter ? after[field] : undefined;
        return {
            field,
            before: beforeValue,
            after: afterValue,
            changed: inBefore !== inAfter || !sameValue(beforeValue, afterValue),
        };
    });
};

/**
 * A recorded value as text: `—` for none, a string as it is, JSON for everything else. Values
 * come from a JSON column, so JSON writes a number, a boolean and a structure the way they read.
 */
export const formatAuditValue = (value: unknown): string => {
    if (value === undefined || value === null) {
        return '—';
    }
    if (typeof value === 'string') {
        return value;
    }
    return JSON.stringify(value);
};
